import { Injectable, inject } from '@angular/core';
import { SupabaseClient, isAuthApiError, isAuthSessionMissingError } from '@supabase/supabase-js';
import { User } from '@app/model/user';
import { PermissionService } from '../permission-service/permission.service';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';
import { AdminTopupService } from '../admin-topup-service/admin-topup.service';
import { formatCoin } from '@app/utils/coin-formatter';
import { fetchAllRows } from '@app/utils/fetch-all-rows';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private supabaseClient: SupabaseClient;

  constructor(
    private permissionService: PermissionService,
    private adminTopupService: AdminTopupService
  ) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  // 分頁抓完全部使用者，避免超過 Supabase 單次回傳上限時被截斷
  async getAllUser() {
    return fetchAllRows<User>((from, to) =>
      this.supabaseClient.from('users').select('*', { count: 'exact' }).order('id').range(from, to));
  }

  async getAllPersonalUser() {
    const res = await this.supabaseClient.from('users').select('*').eq('account_type', 'personal');
    return res;
  }

  async getAllOrganizationUser() {
    const res = await this.supabaseClient.from('users').select('*').eq('account_type', 'organization');
    return res;
  }

  async getAllCharityOrganizationUser() {
    const res = await this.supabaseClient.from('users').select('*').eq('account_type', 'organization').eq('is_charity', true);
    return res;
  }

  async getUserNameById(userId: any) {
    const res = await this.supabaseClient.from('users').select('name').eq('id', userId);
    return res;
  }

  async getUserId() {
    const res = await this.supabaseClient.auth.getUser();
    const userId = res.data.user?.id;
    return userId;
  }

  async getUserAdminsId() {
    const res = await this.supabaseClient
      .from('permission_manage')
      .select('admins')
      .eq('permission_title_en', 'user_manage_permission')
      .single();
    const adminIds = res.data?.admins || [];
    return adminIds;
  }

  async getAdminIdsForAccountAdmin() {
    const accountAdminsIdRes = await this.permissionService.getPermissionAdminsIdByPermissionTitle('user_manage_permission');
    return accountAdminsIdRes;
  }

  async getUserInfo(userId: any) {
    const res = await this.supabaseClient.from('users').select('*').eq('id', userId);
    return res;
  }

  async getUser() {
    const { data, error } = await this.supabaseClient.auth.getUser();
    if (error || !data.user) {
      return { data: null, error: error ?? new Error('目前沒有有效的登入使用者') };
    }

    return this.getUserInfo(data.user.id);
  }

  async getAllAdmins() {
    const res = await this.supabaseClient.from('users').select('*').eq('is_admin', true);
    return res;
  }

  async getSuperAdminId() {
    const { data, error } = await this.supabaseClient
      .from('users')
      .select('id')
      .eq('is_super_admin', true);

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('尚未設定系統金庫帳號');
    }

    if (data.length > 1) {
      throw new Error('系統金庫帳號設定異常：Super Admin 不可超過一人');
    }

    return data[0].id;
  }

  async getUserCompletedTask(userId: any) {
    const res = await this.supabaseClient.from('users').select('task_completed').eq('id', userId);
    return res?.data?.[0].task_completed;
  }

  async payToUserByUserIdForRegis(userId: any, costTime: number) {
    this.assertPositiveAmount(costTime, '初始支付');

    const { data, error } = await this.supabaseClient.rpc('grant_registration_initial_payment', {
      target_user_id: userId,
      initial_amount: formatCoin(costTime)
    });

    if (error) {
      throw error;
    }

    return data as { granted?: boolean; reason?: string; amount?: number };
  }

  async refundTaskDepositOnce(taskId: string, reason: string = 'task_refund') {
    const { data, error } = await this.supabaseClient.rpc('refund_task_deposit_once', {
      target_task_id: taskId,
      refund_reason: reason
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as { refunded?: boolean; reason?: string; amount?: number };
  }

  async refundSupplyMatchmakingOnce(taskId: string, acceptorId: string, reason: string = 'supply_matchmaking_refund') {
    const { data, error } = await this.supabaseClient.rpc('refund_supply_matchmaking_once', {
      target_task_id: taskId,
      target_acceptor_id: acceptorId,
      refund_reason: reason
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as { refunded?: boolean; reason?: string; amount?: number };
  }

  async checkIsAdmin() {
    const res = await this.supabaseClient.auth.getUser();
    const userId = res.data.user?.id;
    const isAdmin = (await this.getUserInfo(userId))?.data?.[0].is_admin;
    return isAdmin;
  }

  async transfer(senderID: any, getterId: any, costTime: number, recordType: string = '轉帳', note: string = '', transferType: string = 'transfer') {
    this.assertPositiveAmount(costTime, '轉帳');
    await this.requireCurrentUserCanTransferFrom(senderID);

    if (senderID === getterId) {
      throw new Error('不可轉帳給自己');
    }

    await this.createCoinTransfer(senderID, getterId, costTime, recordType, note, transferType);
  }

  async donate(senderID: string, receiverId: string, amount: number, recordType: string = '捐贈', note: string = '', transferType: string = 'donate') {
    this.assertPositiveAmount(amount, '捐贈');
    await this.requireCurrentUserCanTransferFrom(senderID);

    if (senderID === receiverId) {
      throw new Error('不可捐贈給自己');
    }

    await this.createCoinTransfer(senderID, receiverId, amount, recordType, note, transferType);
  }

  async updateUserAvatarUrl(userId: any, avatarUrl: string) {
    const newAvatarUrl = avatarUrl;
    const { error } = await this.supabaseClient.from('users').update({ avatar_url: newAvatarUrl }).eq('id', userId);
    if (error) throw error;
  }

  async updateUserInfo(user: User) {
    const { error } = await this.supabaseClient.from('users').update({ name: user.name, address: user.address, phone: user.phone, email: user.email }).eq('id', user.id);
    if (error) throw error;
  }

  async updateUserIsAdmin(user: User) {
    await this.requireCurrentUserPermission('user_manage_permission');

    const { error } = await this.supabaseClient.rpc('set_user_admin_status', {
      target_user_id: user.id,
      target_is_admin: user.is_admin
    });

    if (error) {
      throw error;
    }
  }

  async updateUserIsCharity(user: User) {
    await this.requireCurrentUserPermission('user_manage_permission');

    const { error } = await this.supabaseClient.from('users').update({ is_charity: user.is_charity }).eq('id', user.id);
    if (error) throw error;
  }

  async deleteUser(userId: any) {
    return await this.removeUser(userId);
  }

  async resetAvatar(userId: any) {
    const newAvatarUrl = '/public/assets/defaults/default_avatar.jpg';
    await this.supabaseClient.from('users').update({ avatar_url: newAvatarUrl }).eq('id', userId);
  }

  async checkIsConfirm() {
    const res = await this.getUser();
    return res?.error == null && res?.data?.[0]?.is_confirm === true;
  }

  /**
   * 區分「確定未通過審核 / 已失去登入」與「暫時查不到」：
   * 網路中斷或 token 正在換新時回傳 unknown，呼叫端不應因此登出使用者。
   */
  async getConfirmationStatus(): Promise<'confirmed' | 'unconfirmed' | 'signed_out' | 'unknown'> {
    const { data, error } = await this.supabaseClient.auth.getUser();
    if (error || !data.user) {
      if (isAuthSessionMissingError(error) || (isAuthApiError(error) && [401, 403].includes(error.status))) {
        return 'signed_out';
      }
      return 'unknown';
    }

    const profileRes = await this.getUserInfo(data.user.id);
    if (profileRes.error) {
      return 'unknown';
    }

    return profileRes.data?.[0]?.is_confirm === true ? 'confirmed' : 'unconfirmed';
  }

  async confirmUser(userId: string) {
    await this.requireCurrentUserPermission('user_manage_permission');

    const { error } = await this.supabaseClient.from('users').update({ is_confirm: true }).eq('id', userId);
    if (error) throw error;
  }

  async unconfirmUser(userId: string) {
    await this.requireCurrentUserPermission('user_manage_permission');

    const { error } = await this.supabaseClient.from('users').update({ is_confirm: false }).eq('id', userId);
    if (error) throw error;
  }

  async checkUserCompleteTask() {
    const userId = await this.getUserId();
    const userCompletedTask = await this.getUserCompletedTask(userId);
    return userCompletedTask;
  }

  async resetUserPassword(newPassword: string) {
    const { data, error } = await this.supabaseClient.auth.updateUser({
      password: newPassword
    });
    if (error) {
      console.error('更新密碼失敗:', error);
      throw error;
    }
    return { success: true, data };
  }

  async adminResetUserPassword(user: User) {
    try {
      const userId = user.id;
      if (userId) {
        return await this.invokeAdminFunction('admin-reset-user-password', { userId });
      } else {
        throw new Error('User ID is not available.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  regis(newUser: User) {
    return this.supabaseClient.from('users').insert([
      { name: newUser.name, address: newUser.address, phone: newUser.phone, email: newUser.email, account_type: newUser.account_type, certifications_description: newUser.certifications_description, specialty_description: newUser.specialty_description }
    ]);
  }

  login(account: string, password: string) {
    return this.supabaseClient.from('users').select('*').eq('account', account).eq('password', password);
  }

  get user() {
    return this.supabaseClient.auth.getUser();
  }

  signUp(phone: string, password: string) {
    return this.supabaseClient.auth.signUp({ phone: phone, password: password });
  }

  signIn(account: string, password: string) {
    return this.supabaseClient.auth.signInWithPassword({ phone: account, password: password });
  }

  signOut(scope: 'local' | 'global' = 'local') {
    return this.supabaseClient.auth.signOut({ scope });
  }

  async removeUser(userId: string) {
    return await this.invokeAdminFunction('admin-delete-user', { userId });
  }

  private async invokeAdminFunction(functionName: string, body: Record<string, unknown>) {
    const { data, error, response } = await this.supabaseClient.functions.invoke(functionName, { body });

    if (error) {
      throw new Error(await this.getFunctionErrorMessage(error, response));
    }

    if (data && typeof data === 'object' && 'error' in data) {
      throw new Error(String(data.error));
    }

    return data;
  }

  private async getFunctionErrorMessage(error: unknown, response?: Response): Promise<string> {
    const context = (error as { context?: Response })?.context;
    const errorResponse = response ?? context;

    if (errorResponse) {
      const status = errorResponse.status;
      const statusText = errorResponse.statusText;
      const responseText = await errorResponse.clone().text().catch(() => '');

      if (responseText) {
        try {
          const parsed = JSON.parse(responseText);
          return parsed?.error
            ? `Edge Function ${status} ${statusText}: ${parsed.error}`
            : `Edge Function ${status} ${statusText}: ${responseText}`;
        } catch {
          return `Edge Function ${status} ${statusText}: ${responseText}`;
        }
      }

      return `Edge Function ${status} ${statusText}`;
    }

    return error instanceof Error ? error.message : String(error);
  }

  private assertPositiveAmount(amount: number, operation: string) {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`${operation}金額必須大於 0`);
    }
  }

  private async requireCurrentUserCanTransferFrom(senderId: string) {
    const currentUserId = await this.getUserId();
    if (!currentUserId) {
      throw new Error('Unauthorized');
    }

    if (currentUserId === senderId) {
      return;
    }

    const currentUser = (await this.getUserInfo(currentUserId))?.data?.[0];
    if (!currentUser?.is_super_admin) {
      throw new Error('Forbidden');
    }
  }

  private async createCoinTransfer(
    senderId: string,
    receiverId: string,
    amount: number,
    recordType: string,
    note: string,
    transferType: string
  ) {
    const { error } = await this.supabaseClient.rpc('create_coin_transfer', {
      sender_id: senderId,
      receiver_id: receiverId,
      transfer_amount: formatCoin(amount),
      record_type: recordType,
      transfer_note: note || '',
      transfer_type: transferType || 'transfer'
    });

    if (error) {
      throw error;
    }
  }

  private async requireCurrentUserPermission(permissionTitle: string) {
    const currentUserId = await this.getUserId();
    if (!currentUserId) {
      throw new Error('Unauthorized');
    }

    const currentUser = (await this.getUserInfo(currentUserId))?.data?.[0];
    if (currentUser?.is_super_admin) {
      return;
    }

    if (!currentUser?.is_admin) {
      throw new Error('Forbidden');
    }

    const allowedAdminIds = await this.permissionService.getPermissionAdminsIdByPermissionTitle(permissionTitle);
    if (!allowedAdminIds.includes(currentUserId)) {
      throw new Error('Forbidden');
    }
  }

  /**
   * 管理員加值功能
   * 只有超級管理員可以執行此操作
   * @param targetUserId 要加值的目標用戶ID（可以是自己或其他用戶）
   * @param amount 加值金額
   * @param note 加值備註說明
   * @param topupType 加值類型：'system_init' | 'manual_topup' | 'compensation' | 'adjustment'
   * @returns 加值結果
   */
  async adminTopup(targetUserId: string, amount: number, note: string, topupType: string = 'manual_topup') {
    try {
      // 1. 檢查當前用戶是否為超級管理員
      const currentUserId = await this.getUserId();
      const currentUserInfo = await this.getUserInfo(currentUserId);
      const isSuperAdmin = currentUserInfo?.data?.[0].is_super_admin;

      if (!isSuperAdmin) {
        return {
          success: false,
          error: '只有超級管理員可以執行加值操作'
        };
      }

      // 2. 檢查金額是否有效
      if (amount <= 0) {
        return {
          success: false,
          error: '加值金額必須大於 0'
        };
      }

      // 3. 檢查目標用戶是否存在
      const targetUserInfo = await this.getUserInfo(targetUserId);
      if (!targetUserInfo?.data || targetUserInfo.data.length === 0) {
        return {
          success: false,
          error: '目標用戶不存在'
        };
      }

      // 4. 執行加值：交給資料庫 RPC 原子完成餘額更新與審計紀錄
      const { data, error } = await this.supabaseClient.rpc('admin_topup_coin', {
        target_user_id: targetUserId,
        amount,
        note,
        topup_type: topupType
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        message: `成功為用戶加值 ${amount} 時間幣`,
        data: {
          targetUserId,
          amount,
          newBalance: data?.new_balance ?? targetUserInfo.data[0].total_time,
          topupType,
          note
        }
      };

    } catch (error) {
      console.error('加值操作失敗:', error);
      return {
        success: false,
        error: '加值操作失敗，請稍後再試'
      };
    }
  }

  /**
   * 取得特定用戶的加值歷史記錄
   * @param userId 用戶ID
   * @returns 加值記錄列表
   */
  async getUserTopupHistory(userId: string) {
    return await this.adminTopupService.getTopupRecordsByTargetUserId(userId);
  }

  /**
   * 取得所有加值記錄（僅供管理員使用）
   * @returns 所有加值記錄
   */
  async getAllTopupRecords() {
    const currentUserId = await this.getUserId();
    const currentUserInfo = await this.getUserInfo(currentUserId);
    const isSuperAdmin = currentUserInfo?.data?.[0].is_super_admin;

    if (!isSuperAdmin) {
      return null;
    }

    return await this.adminTopupService.getAllTopupRecords();
  }
}
